import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DatabaseIcon, ShieldCheckIcon, ZapIcon, CheckIcon, XIcon, ArrowRightIcon } from 'lucide-react';
const tabs = [{
  id: 'data',
  icon: DatabaseIcon,
  title: '50+ enriched data fields',
  description: 'Go beyond name and address. Get direct emails, social profiles, revenue estimates, and owner contact info — all in a single API call, no extra charges.'
}, {
  id: 'fresh',
  icon: ZapIcon,
  title: 'Real-time freshness',
  description: 'Our local business data API continuously crawls and verifies millions of local businesses to ensure you get the most accurate, up-to-date information available.'
}];
export function AlternativeSection() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-527">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-528">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-529">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-530">
            The most complete{' '}
            <span className="text-blue-600" data-id="element-531">local business data</span> API
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-532">
            Why pay more for less? Openmart gives you comprehensive local
            business data at a fraction of the cost — built for developers,
            marketers, and sales teams who need results fast.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-533">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-534">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-535">
                  <div className="flex items-start gap-6" data-id="element-536">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-537">
                      <tab.icon className="w-6 h-6" data-id="element-538" />
                    </div>
                    <div data-id="element-539">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-540">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-541">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-542">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-543">
              <AnimatePresence mode="wait" data-id="element-544">
                {activeTab === 'data' && <motion.div key="data" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-545">
                    <div className="flex items-center justify-between mb-6 px-2" data-id="element-546">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-547">
                        Google Places API
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-548" />
                      <div className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-549">
                        Openmart API
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-550">
                      <div className="bg-white p-5 rounded-2xl font-mono text-xs text-slate-500 space-y-2.5 shadow-sm" data-id="element-551">
                        <div data-id="element-552">{`"name": "..."`}</div>
                        <div data-id="element-553">{`"address": "..."`}</div>
                        <div data-id="element-554">{`"phone": "..."`}</div>
                        <div data-id="element-555">{`"rating": "..."`}</div>
                        <div className="text-slate-300 italic pt-3" data-id="element-556">
                          {"// That's it — no emails,"}
                        </div>
                        <div className="text-slate-300 italic" data-id="element-557">
                          {'// no socials, no storage'}
                        </div>
                      </div>
                      <div className="bg-white p-5 rounded-2xl font-mono text-xs text-slate-500 space-y-2.5 shadow-sm border border-blue-100" data-id="element-558">
                        <div data-id="element-559">{`"id": "loc_..."`}</div>
                        <div data-id="element-560">{`"name": "..."`}</div>
                        <div data-id="element-561">{`"tags": ["cafe", ...]`}</div>
                        <div data-id="element-562">{`"phones": ["+1..."]`}</div>
                        <div className="text-green-600 font-semibold bg-green-50 px-1.5 py-0.5 rounded" data-id="element-563">{`"email": "hello@..."`}</div>
                        <div className="text-green-600 font-semibold bg-green-50 px-1.5 py-0.5 rounded" data-id="element-564">{`"socials": {...}`}</div>
                        <div className="text-green-600 font-semibold bg-green-50 px-1.5 py-0.5 rounded" data-id="element-565">{`"rating": 4.8`}</div>
                        <div className="text-green-600 font-semibold bg-green-50 px-1.5 py-0.5 rounded" data-id="element-566">{`"website": "..."`}</div>
                        <div className="text-blue-400 italic pt-1" data-id="element-567">
                          {'// + 40 more fields'}
                        </div>
                      </div>
                    </div>
                  </motion.div>}
                {activeTab === 'tos' && <motion.div key="tos" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm" data-id="element-568">
                    <h4 className="font-bold text-xl text-slate-900 mb-8 text-center" data-id="element-569">
                      What you can do with the data
                    </h4>
                    <ul className="space-y-6" data-id="element-570">
                      {[{
                    text: 'Store data in your own database',
                    google: false,
                    openmart: true
                  }, {
                    text: 'Use for outbound sales & lead gen',
                    google: false,
                    openmart: true
                  }, {
                    text: 'Display on Mapbox or Leaflet',
                    google: false,
                    openmart: true
                  }, {
                    text: 'Cache results indefinitely',
                    google: false,
                    openmart: true
                  }].map((item, i) => <li key={i} className="flex items-center justify-between text-base" data-id="element-571">
                          <span className="text-slate-700 font-medium" data-id="element-572">
                            {item.text}
                          </span>
                          <div className="flex gap-6 w-24 justify-end" data-id="element-573">
                            <div className="w-8 flex justify-center" data-id="element-574">
                              <XIcon className="w-5 h-5 text-slate-300" data-id="element-575" />
                            </div>
                            <div className="w-8 flex justify-center" data-id="element-576">
                              <CheckIcon className="w-5 h-5 text-blue-600" data-id="element-577" />
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </motion.div>}
                {activeTab === 'fresh' && <motion.div key="fresh" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-578">
                    <div className="space-y-12 bg-white p-10 rounded-3xl shadow-sm" data-id="element-579">
                      <div data-id="element-580">
                        <div className="flex justify-between text-base font-bold mb-4" data-id="element-581">
                          <span className="text-slate-500" data-id="element-582">
                            Legacy providers
                          </span>
                          <span className="text-slate-400 font-normal" data-id="element-583">
                            Updates every 90+ days
                          </span>
                        </div>

                        <div className="h-4 bg-slate-100 rounded-full overflow-hidden" data-id="element-584">
                          <div className="h-full bg-slate-300 rounded-full w-[30%]" data-id="element-585" />
                        </div>
                      </div>
                      <div data-id="element-586">
                        <div className="flex justify-between text-base font-bold mb-4" data-id="element-587">
                          <span className="text-blue-600" data-id="element-588">Openmart API</span>
                          <span className="text-blue-500 font-normal" data-id="element-589">
                            Weekly data updates
                          </span>
                        </div>

                        <div className="h-4 bg-blue-50 rounded-full overflow-hidden" data-id="element-590">
                          <div className="h-full bg-blue-500 rounded-full w-[95%]" data-id="element-591" />
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}